import React, { Fragment, useContext, useEffect, useRef } from "react";
import ChatAppContext from "../../../Chat";
import avatar from "../../../../src/assets/images/avatar.png";
import { format, isToday, isYesterday } from "date-fns";
import { formatTimeAMPM, convertHtmlToJsx } from "../../Common/CommonFunction";
import Spinner from "../../Common/Spinner";
import ChatMessageAttachment from "../../Common/ChatMessageAttachment";
import { JOBSEEKER_PROFILE_IMAGE } from "../../../Constant";
import {
  isSenderAdmin,
  isSenderJobseeker,
} from "../../../utils/chatSenders";

const supportAvatarUrl = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(
    (name || "Support").slice(0, 40)
  )}&background=0D8ABC&color=fff&size=128`;

/** Plain-text lines for ticket meta answers (avoid injecting HTML from stored ticket data). */
const formatTicketMetaAnswer = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line, i, arr) => (
      <Fragment key={i}>
        {line}
        {i < arr.length - 1 ? <br /> : null}
      </Fragment>
    ));

const ChatMessage = () => {
  const chatContainerRef = useRef();
  const {
    messages = {},
    chatLoading,
    selectedUser,
    currentUser,
    ticketMeta = [],
  } = useContext(ChatAppContext) || {};

  const senderLabel = (item) => {
    if (isSenderJobseeker(item.sender_type)) return "You";
    if (isSenderAdmin(item.sender_type)) {
      return (
        selectedUser?.recruiter_full_name ||
        selectedUser?.admin_chat_title ||
        "Support"
      )
        .toString()
        .replace(/^Support ·\s*/i, "")
        .trim() || "Support";
    }
    return "Recruiter";
  };

  const bubbleAvatarSrc = (item) => {
    if (isSenderJobseeker(item.sender_type)) {
      return currentUser?.jobseeker_photo ? currentUser.jobseeker_photo : avatar;
    }
    if (isSenderAdmin(item.sender_type)) {
      return supportAvatarUrl(
        selectedUser?.recruiter_full_name || selectedUser?.admin_chat_title
      );
    }
    return selectedUser?.profile_image || avatar;
  };

  const renderDateSeparator = (date) => {
    if (isToday(new Date(date))) {
      return "Today";
    } else if (isYesterday(new Date(date))) {
      return "Yesterday";
    } else {
      return format(new Date(date), "yyyy-MM-dd");
    }
  };

  useEffect(() => {
    // Scroll to the bottom of the chat container when messages change
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, ticketMeta]);

  return (
    <Fragment>
      {chatLoading ? (
        <Spinner />
      ) : (
        <div
          className="chat-history chat-msg-box custom-scrollbar"
          ref={chatContainerRef}
        >
          <ul className="simple-list">
            <li className="clearfix border-0 mb-4">
              <div
                style={{ width: "90%", margin: "auto" }}
                className="message other-message"
              >
                <h6>Chat Community Guidelines</h6>
                <p>
                  Welcome to our chat community! To maintain a positive
                  environment for all members, please adhere to the following
                  guidelines:
                </p>
                <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                  <li>Be respectful and courteous in all interactions.</li>
                  <li>
                    Keep discussions relevant to the chat's purpose or topic.
                  </li>
                  <li>
                    Avoid spamming, chain messages, or irrelevant forwards.
                  </li>
                  <li>
                    Respect everyone's privacy and refrain from sharing personal
                    information without consent.
                  </li>
                  <li>
                    Use appropriate language and avoid offensive or
                    inappropriate content.
                  </li>
                  <li>
                    Report any violations of these guidelines to the chat
                    moderators.
                  </li>
                  <li>
                    Remember that all chat members are subject to the chat
                    platform's Terms of Service.
                  </li>
                </ul>
                <p>
                  Thank you for contributing to a positive and enjoyable chat
                  experience!
                </p>
              </div>
            </li>
            {Array.isArray(ticketMeta) && ticketMeta.length > 0 ? (
              <li className="clearfix border-0 mb-4">
                <div
                  style={{ width: "90%", margin: "auto" }}
                  className="message other-message"
                >
                  <h6>Your ticket details</h6>
                  <p className="small text-muted mb-2">
                    Information saved when you raised this support ticket
                    (including answers from the form).
                  </p>
                  <dl className="mb-0 small">
                    {ticketMeta.map((row, idx) => (
                      <Fragment
                        key={
                          row.meta_id != null && row.meta_id !== ""
                            ? `tm-${row.meta_id}`
                            : `tm-${idx}`
                        }
                      >
                        <dt className="fw-semibold mt-2 mb-1">
                          {row.question || "—"}
                        </dt>
                        <dd className="mb-0 ms-0 text-break">
                          {row.file_url ? (
                            <a
                              href={row.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.answer || "Download"}
                            </a>
                          ) : (
                            formatTicketMetaAnswer(row.answer)
                          )}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </div>
              </li>
            ) : null}
            {Object.keys(messages).length > 0 ? (
              Object.keys(messages).map((date) => (
                <Fragment key={date}>
                  <li className="date-separator mb-4 text-center">
                    <span className="badge bg-primary">
                      {renderDateSeparator(date)}
                    </span>
                  </li>
                  {messages[date].map((item, index) => (
                    <li className="clearfix border-0" key={item.message_id}>
                      <div
                        style={{ width: "400px" }}
                        className={`message ${!isSenderJobseeker(item.sender_type)
                            ? "my-message"
                            : "other-message float-end"
                          }`}
                      >
                        <img
                          className={`rounded-circle ${!isSenderJobseeker(item.sender_type)
                              ? "float-start"
                              : "float-end"
                            } chat-user-img img-30`}
                          //  src={selectedUser?.profile_image || avatar}
                          src={bubbleAvatarSrc(item)}

                          alt=""
                        />
                        <div className="message-data text-end">
                          <span className="message-data-time">
                            <span className="text-muted small me-1">
                              {senderLabel(item)} ·
                            </span>
                            {formatTimeAMPM(item.created_at)}
                          </span>
                        </div>
                        {convertHtmlToJsx(item.message_text)}
                        <ChatMessageAttachment item={item} />
                      </div>
                    </li>
                  ))}
                </Fragment>
              ))
            ) : (
              <div>
                <img
                  className="img-fluid"
                  src="/path/to/your/image.png"
                  alt="start conversation"
                />
              </div>
            )}
          </ul>
        </div>
      )}
    </Fragment>
  );
};

export default ChatMessage;
